import type { JSX } from 'react';

import { Button } from '@gearbelt/ui/components';
import { NavLink } from 'react-router-dom';

import ArrowDownIcon from '@/components/icons/arrow-down';
import CheckIcon from '@/components/icons/check';
import GearIcon from '@/components/icons/gear';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { getWorkspaceRoute, useWorkspaceNavigate } from '@/utils/routes';

type TTask = {
  heading: string;
  content: string | JSX.Element;
  status: 'pending' | 'done';
};

const NumberCircle = ({ children, isCompleted }: { children: React.ReactNode; isCompleted: boolean }) => (
  <span
    className={`mr-3.5 flex h-[30px] w-[30px] items-center justify-center rounded-full border text-base ${
      isCompleted ? 'border-default-green text-default-green' : 'border-grey-3'
    }`}>
    {children}
  </span>
);

export const GettingStartedPage = ({
  dashboard,
}: {
  dashboard: {
    asset: number;
    inspection: number;
    logo: boolean;
  };
}) => {
  const navigate = useWorkspaceNavigate();

  const tasks: TTask[] = [
    {
      heading: 'Access real time data by submitting a form ',
      content: (
        <>
          <p className="mb-2">Choose from ready-to-use templates or customise your forms to experience the real time benefits.</p>
          <p className="mb-2">Download the mobile app to your device, fill out any of your created forms, or submit it via our platform.</p>
          <p className="mb-2">You can then view your information in the form submission area, or the fleet servicing dashboard.</p>
        </>
      ),
      status: dashboard.asset > 3 ? 'done' : 'pending',
    },
    {
      heading: 'Import your company logo for professional submissions',
      content: (
        <>
          <p className="mb-2">
            Upload your <NavLink to={getWorkspaceRoute('/settings/workspace/branding')}>company logo</NavLink> via the settings page and invite your team to
            collaborate.
          </p>
          {/* <p className='mb-2'>The <NavLink to='/settings/profile'>Settings page</NavLink> allows you to invite other users, configure your notification preferences, and manage your profile</p> */}
          <Button type="button" size="lg" className="my-2" onClick={() => navigate('/settings/workspace/branding')} icon={<GearIcon />}>
            Settings
          </Button>
        </>
      ),
      status: dashboard.logo ? 'done' : 'pending',
    },
  ];

  const progress = Math.round((tasks.filter((task) => task.status === 'done').length * 100) / tasks.length);

  return (
    <>
      <div className="flex justify-center">
        <div className="mt-2 w-full max-w-6xl">
          <div className="rounded-lg">
            <div className="px-6 py-4">
              <h4 className="mb-2 text-xl font-semibold">Getting Started</h4>
              <div className="bg-grey-3 h-1.5 w-full rounded-full">
                <div
                  className="bg-default-blue h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                  role="progressbar"
                  aria-label="Progress indicator"
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}></div>
              </div>
            </div>
            <div className="p-6">
              <Accordion type="multiple" defaultValue={tasks.map((_, i) => i.toString())} className="space-y-4">
                {tasks.map((task, i) => {
                  const pending = task.status === 'pending';

                  return (
                    <AccordionItem value={i.toString()} key={i} className="bg-white">
                      <AccordionTrigger className="group flex w-full items-center justify-between p-4">
                        <div className="flex w-full items-center">
                          <h3 className="text-grey-9 flex items-center text-lg font-medium">
                            <NumberCircle isCompleted={!pending}>{pending ? i + 1 : <CheckIcon />}</NumberCircle>
                            {task.heading}
                          </h3>
                        </div>

                        <ArrowDownIcon className="size-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <section className="text-grey-7 pl-11 text-base">{task.content}</section>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
